import React, { createContext, useContext, useState, useEffect } from 'react';

// Default configuration
const defaultConfig = {
  apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  appName: process.env.REACT_APP_NAME || 'Teacher Web',
  environment: process.env.NODE_ENV || 'development',
  debugMode: process.env.NODE_ENV === 'development',
  features: {
    enableSocialLogin: process.env.REACT_APP_ENABLE_SOCIAL_LOGIN === 'true',
    enableNotifications: process.env.REACT_APP_ENABLE_NOTIFICATIONS !== 'false',
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true'
  },
  limits: {
    maxFileSize: parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 5242880, // 5MB
    maxStudentsPerClass: parseInt(process.env.REACT_APP_MAX_STUDENTS_PER_CLASS) || 30,
    sessionTimeout: parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 3600000 // 1 hour
  },
  ui: {
    defaultTheme: process.env.REACT_APP_DEFAULT_THEME || 'light',
    defaultLanguage: process.env.REACT_APP_DEFAULT_LANGUAGE || 'en',
  }
};

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadRemoteConfig = async () => {
      if (config.environment === 'development') {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${config.apiUrl}/config`);
        
        if (!response.ok) {
          throw new Error('Failed to load configuration');
        }

        const remoteConfig = await response.json();
        
        setConfig(prevConfig => ({
          ...prevConfig,
          ...remoteConfig,
          features: { ...prevConfig.features, ...remoteConfig.features },
          limits: { ...prevConfig.limits, ...remoteConfig.limits },
          ui: { ...prevConfig.ui, ...remoteConfig.ui }
        }));

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRemoteConfig();
  }, [config.apiUrl, config.environment]);

  const updateConfig = (updates) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      ...updates
    }));
  };

  const isFeatureEnabled = (featureName) => {
    return config.features[featureName] || false;
  };

  const getConfigValue = (path, defaultValue = null) => {
    const keys = path.split('.');
    let value = config;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
    
    return value;
  };

  const contextValue = {
    config,
    loading,
    error,
    
    isDevelopment: config.environment === 'development',
    isProduction: config.environment === 'production',
    
    updateConfig,
    isFeatureEnabled,
    getConfigValue
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfigContext = () => {
  const context = useContext(ConfigContext);
  
  if (!context) {
    throw new Error('useConfigContext must be used within a ConfigProvider');
  }
  
  return context;
};

export default ConfigContext;